import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { ApolloError } from "@apollo/client";
import {
  rootFieldName,
  vercelFlagPathSeparator,
} from "@hypertune/shared-internal";
import ContainerWithLogo from "../../components/container/ContainerWithLogo";
import { useVercelSsoMutation } from "../../generated/graphql";
import getErrorMessage from "../../lib/query/getErrorMessage";
import useLoginRedirectQuery from "../../lib/hooks/useLoginRedirectQuery";
import {
  selectedFieldPathParamKey,
  urlFieldPathSeparator,
} from "../project/logicHooks";
import { getProjectUrl } from "../project/projectHooks";
import { supportPath } from "../../lib/constants";

export default function VercelSsoPage(): React.ReactElement {
  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");
  const projectId = searchParams.get("resource_id");
  const support = searchParams.get("support");
  const experimentationItemId = searchParams.get("experimentation_item_id");

  const [vercelSsoMutation, { error: apolloError }] = useVercelSsoMutation();

  const auth = getAuth();

  const redirectTo = getRedirectTo(support, projectId, experimentationItemId);
  const redirect = useLoginRedirectQuery(redirectTo ?? undefined);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const login = useCallback(
    async function login(): Promise<void> {
      if (!code) {
        throw new Error("No code.");
      }

      const response = await vercelSsoMutation({
        variables: { input: { code } },
      });

      if (!response.data) {
        throw new Error("No response.");
      }

      const firebaseCustomToken = response.data?.vercelSso.firebaseCustomToken;

      await signInWithCustomToken(auth, firebaseCustomToken);
      await redirect();
    },
    [code, vercelSsoMutation, auth, redirect]
  );

  useEffect(() => {
    document.title = "Logging in with Vercel...";

    login().catch((loginError) => {
      setErrorMessage(getErrorMessage(loginError));
    });
  }, [login]);

  return (
    <ContainerWithLogo>
      <div style={{ textAlign: "center" }}>
        {getMessage(apolloError, errorMessage)}
      </div>
    </ContainerWithLogo>
  );
}

function getRedirectTo(
  support: string | null,
  projectId: string | null,
  experimentationItemId: string | null
): string | null {
  if (support) {
    return supportPath;
  }

  if (!projectId) {
    return null;
  }

  const searchParams = new URLSearchParams();

  if (experimentationItemId) {
    const selectedFieldPath = `${rootFieldName}${urlFieldPathSeparator}${experimentationItemId.replaceAll(vercelFlagPathSeparator, urlFieldPathSeparator)}`;

    searchParams.set(selectedFieldPathParamKey, selectedFieldPath);
  }

  return getProjectUrl({ projectId, selectedView: "logic", searchParams });
}

function getMessage(
  apolloError: ApolloError | undefined,
  errorMessage: string | null
): string {
  if (apolloError) {
    return getErrorMessage(apolloError);
  }

  if (errorMessage) {
    return errorMessage;
  }

  return "Logging in with Vercel...";
}
